import React, { useEffect, useState } from "react";
import AuditLogService from "../../services/AuditLogService";

const AuditLogPopup = ({
  open = false,
  subjectId = null,
  subjectLabel = "ID",
  subjectFilterKey = "visit_id",
  onClose,
  Toast,
  Loader,
}) => {
  const [show, setShow] = useState(open);
  const [selectedSubjectId, setSelectedSubjectId] = useState(subjectId);

  const [auditSearchQuery, setAuditSearchQuery] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [auditDateFilter, setAuditDateFilter] = useState("all-time");
  const [expandedAuditItems, setExpandedAuditItems] = useState({});
  const [auditLogEntries, setAuditLogEntries] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPerPage, setAuditPerPage] = useState(25);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditTotalRecords, setAuditTotalRecords] = useState(0);

  useEffect(() => {
    setShow(open);
    if (open) setSelectedSubjectId(subjectId);
  }, [open, subjectId]);

  const getRelativeDateLabel = (dateValue) => {
    if (!dateValue) return "";

    const now = new Date();
    const date = new Date(dateValue);
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const startOfDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const diffInDays = Math.floor(
      (startOfToday - startOfDate) / 86400000
    );
    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    return "";
  };

  const formatJsonForBlock = (value) => {
    if (value === null || value === undefined || value === "") return "{}";
    if (typeof value === "string") {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch (e) {
        return value;
      }
    }
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const humanizeModelType = (value = "") => {
    const text = String(value || "").trim();
    if (!text) return "Unknown";
    return text
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const mapActionToUiMeta = (action = "") => {
    const normalized = String(action || "").toUpperCase();
    if (normalized === "CREATE") {
      return {
        actionType: "note-added",
        badgeIconClass: "fa-regular fa-note-sticky me-1",
        badgeLabel: "Create",
      };
    }
    if (normalized === "DELETE") {
      return {
        actionType: "other",
        badgeIconClass: "fa-solid fa-trash me-1",
        badgeLabel: "Delete",
      };
    }
    return {
      actionType: "field-update",
      badgeIconClass: "fa-solid fa-pen-to-square me-1",
      badgeLabel: "Update",
    };
  };

  const getDateRangeForFilter = (filterKey) => {
    const now = new Date();
    const formatMDY = (dateObj) => {
      const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
      const dd = String(dateObj.getDate()).padStart(2, "0");
      const yyyy = dateObj.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    };

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
    const startOfLast7Days = new Date(startOfToday.getTime() - 6 * 86400000);
    const startOfLast30Days = new Date(startOfToday.getTime() - 29 * 86400000);

    if (filterKey === "today") {
      const d = formatMDY(startOfToday);
      return { date_from: d, date_to: d };
    }
    if (filterKey === "yesterday") {
      const d = formatMDY(startOfYesterday);
      return { date_from: d, date_to: d };
    }
    if (filterKey === "last-7-days") {
      return { date_from: formatMDY(startOfLast7Days), date_to: formatMDY(now) };
    }
    if (filterKey === "last-30-days") {
      return { date_from: formatMDY(startOfLast30Days), date_to: formatMDY(now) };
    }
    return {};
  };

  const fetchAuditLogs = async (page = 1, perPage = 25) => {
    if (!selectedSubjectId) return;

    setAuditLogLoading(true);
    Loader?.(true);
    try {
      const dateRangeQuery = getDateRangeForFilter(auditDateFilter);
      const actionQuery =
        auditActionFilter && auditActionFilter !== "all"
          ? { action: String(auditActionFilter).toLowerCase() }
          : {};

      const query = {
        page,
        perPage,
        [subjectFilterKey]: selectedSubjectId,
        ...(auditSearchQuery?.trim() ? { search_query: auditSearchQuery.trim() } : {}),
        ...actionQuery,
        ...dateRangeQuery,
      };

      const response = await AuditLogService.getAuditLogList(query);
      const apiRows = response?.data?.data || [];
      const pagination = response?.pagination || {};

      const mappedRows = apiRows.map((row, index) => {
        const { actionType, badgeIconClass, badgeLabel } =
          mapActionToUiMeta(row?.action);

        const timestamp = row?.updated_at || row?.created_at;
        const dateObj = timestamp ? new Date(timestamp) : null;
        const timestampMs = dateObj ? dateObj.getTime() : null;

        const time = dateObj
          ? dateObj.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-";

        const date = dateObj
          ? dateObj.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "-";

        const actionLabel = humanizeModelType(
          row?.model_type_name || row?.model_type
        );
        const modelTypeName = row?.model_type_name || row?.model_type || "";
        const actionRaw = String(row?.action || "").toUpperCase();
        const oldJson = formatJsonForBlock(row?.original_data);
        const newJson = formatJsonForBlock(row?.changed_data);

        const updatedBy =
          row?.username ||
          `${row?.updated_by?.first_name || ""} ${
            row?.updated_by?.last_name || ""
          }`.trim() ||
          "System";

        const rawId =
          row?.id || `audit-${Math.random().toString(36).slice(2)}`;
        const safeId = String(rawId).replace(/[^a-zA-Z0-9_-]/g, "");
        const collapseId = `auditItem-${index + 1}-${safeId}`;

        return {
          id: collapseId,
          actionType,
          itemClass: "status-change",
          time,
          date,
          relative: getRelativeDateLabel(timestamp),
          user: updatedBy,
          modelTypeName,
          actionRaw,
          badgeLabel,
          badgeIconClass,
          actionLabel,
          oldJson,
          newJson,
          updatedBy,
          dataSearch: `${updatedBy} ${modelTypeName} ${actionRaw}`.toLowerCase(),
          timestampMs,
        };
      });

      setAuditLogEntries(mappedRows);
      setExpandedAuditItems({});
      setAuditTotalPages(Number(pagination?.total_pages || 1));
      setAuditTotalRecords(Number(pagination?.totalCount || mappedRows.length || 0));
    } catch (error) {
      setAuditLogEntries([]);
      setAuditTotalPages(1);
      setAuditTotalRecords(0);
      Toast?.error?.("Failed to load audit logs");
    } finally {
      setAuditLogLoading(false);
      Loader?.(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setAuditSearchQuery("");
    setAuditActionFilter("all");
    setAuditDateFilter("all-time");
    setExpandedAuditItems({});
    setAuditPage(1);
    setAuditPerPage(25);
    setAuditTotalPages(1);
    setAuditTotalRecords(0);
    setAuditLogEntries([]);
    setAuditLogLoading(false);
  }, [open, selectedSubjectId, subjectFilterKey]);

  const toggleAuditItem = (itemId) => {
    setExpandedAuditItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  useEffect(() => {
    setAuditPage(1);
  }, [auditSearchQuery, auditActionFilter, auditDateFilter]);

  useEffect(() => {
    if (!show || !selectedSubjectId) return;
    fetchAuditLogs(auditPage, auditPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    selectedSubjectId,
    subjectFilterKey,
    auditPage,
    auditPerPage,
    auditSearchQuery,
    auditActionFilter,
    auditDateFilter,
  ]);

  useEffect(() => {
    if (auditPage > auditTotalPages) {
      setAuditPage(auditTotalPages);
    }
  }, [auditPage, auditTotalPages]);

  if (!show) return null;

  const totalPagesNav = Math.max(
    1,
    Number(auditTotalPages) > 0
      ? Number(auditTotalPages)
      : Math.ceil((auditTotalRecords || 0) / Math.max(1, auditPerPage || 25)) || 1
  );
  const maxVisiblePages = 5;
  const halfWindow = Math.floor(maxVisiblePages / 2);
  let pageWindowStart = Math.max(1, auditPage - halfWindow);
  let pageWindowEnd = Math.min(totalPagesNav, pageWindowStart + maxVisiblePages - 1);
  if (pageWindowEnd - pageWindowStart < maxVisiblePages - 1) {
    pageWindowStart = Math.max(1, pageWindowEnd - maxVisiblePages + 1);
  }
  const visiblePageNumbers = [];
  for (let i = pageWindowStart; i <= pageWindowEnd; i++) {
    visiblePageNumbers.push(i);
  }

  const handleAuditNavPage = (page) => {
    if (
      auditLogLoading ||
      page < 1 ||
      page > totalPagesNav ||
      page === auditPage
    ) {
      return;
    }
    setAuditPage(page);
  };

  const handleAuditPerPagePick = (value) => {
    if (auditPerPage === value || auditLogLoading) return;
    setAuditPerPage(value);
    setAuditPage(1);
  };

  return (
    <>
      <div
        className="modal fade show"
        id="auditlogscreen"
        tabIndex="-1"
        aria-labelledby="auditlogscreenmodal"
        style={{ display: "block" }}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-scrollable hResponsive modal-dialog-centered modal-xl">
          <div className="modal-content h-100">
            <div
              className="sh_modalBox d-flex flex-column h-100 overflow-hidden"
              style={{ backgroundColor: "#fff", minHeight: 0 }}
            >
              <div className="modal-header  border-0 pb-0">
                <div className="d-flex flex-column">
                  <h5
                    className="modal-title font-weight-bold mb-0"
                    id="auditlogscreenmodal"
                  >
                    Audit Log
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => onClose?.()}
                ></button>
              </div>

              <div className="modal-header d-flex flex-wrap align-items-center gap-3 pb-3">
                <div className="d-flex w-50 align-items-center gap-3">
                  {/* Search input hidden (parity with visits audit modal); auditSearchQuery state + fetchAuditLogs still support search_query when non-empty */}
                  {/* <div className="form-group w-75 mr-2 mb-0">
                    <input
                      id="auditSearchInput"
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search audit logs..."
                      autoComplete="off"
                      value={auditSearchQuery}
                      onChange={(e) => setAuditSearchQuery(e.target.value)}
                    />
                  </div> */}

                  <select
                    id="auditActionFilter"
                    className="form-control w-25 form-control-solid"
                    aria-label="Action filter"
                    value={auditActionFilter}
                    onChange={(e) => setAuditActionFilter(e.target.value)}
                  >
                    <option value="all">All actions</option>
                    <option value="CREATE">Create</option>
                    <option value="UPDATE">Update</option>
                    <option value="DELETE">Delete</option>
                  </select>
                </div>

                <div className="">
                  <select
                    id="auditDateFilter"
                    className="form-control  form-control-solid"
                    aria-label="Date filter"
                    value={auditDateFilter}
                    onChange={(e) => setAuditDateFilter(e.target.value)}
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last-7-days">Last 7 days</option>
                    <option value="last-30-days">Last 30 days</option>
                    <option value="all-time">All time</option>
                  </select>
                </div>
              </div>

              <div
                className="modal-body pt-0 overflow-auto flex-grow-1 position-relative"
                style={{ minHeight: 0 }}
              >
                <div className="sh_auditlog">
                  <div id="auditTimeline" className="sh_auditlog-timeline">
                    {auditLogEntries.map((entry) => {
                      const isOpen = !!expandedAuditItems[entry.id];
                      return (
                        <div
                          key={entry.id}
                          className={`sh_auditlog-item ${
                            entry.itemClass || entry.actionType
                          }`}
                          data-action={entry.actionType}
                          data-search={entry.dataSearch}
                        >
                          <div className="sh_auditlog-left">
                            <div className="sh_auditlog-time">{entry.time}</div>
                            <div className="sh_auditlog-date">{entry.date}</div>
                            <div className="sh_auditlog-relative">
                              {entry.relative}
                            </div>
                          </div>

                          <div className="sh_auditlog-card">
                            <a
                              className="sh_auditlog-toggle"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#${entry.id}`}
                              aria-expanded={isOpen}
                              aria-controls={entry.id}
                              onClick={(e) => {
                                e.preventDefault();
                                toggleAuditItem(entry.id);
                              }}
                            >
                              <div className="d-flex justify-content-between sh_auditlog-toggle-container">
                                <div className="sh_auditlog-toggle-left">
                                  <div className="sh_auditlog-user">
                                    <i className="fa-solid fa-circle-user"></i>
                                    <span>{entry.user}</span>
                                  </div>
                                  <span className="badge rounded-pill sh_auditlog-badge">
                                    <i className={entry.badgeIconClass}></i>{" "}
                                    {entry.badgeLabel}
                                  </span>
                                </div>
                                <div className="sh_auditlog-toggle-right">
                                  <span className="sh_auditlog-action">
                                    {entry.actionLabel}
                                  </span>
                                </div>
                              </div>
                            </a>

                            <div
                              id={entry.id}
                              className={`collapse${isOpen ? " show" : ""}`}
                            >
                              <div className="sh_auditlog-body">
                                {entry.field ? (
                                  <div className="sh_auditlog-change">
                                    <div className="sh_auditlog-field">
                                      {entry.field}
                                    </div>
                                    <div className="sh_auditlog-chip sh_auditlog-chip-old">
                                      {entry.oldValue}
                                    </div>
                                    <div
                                      className="sh_auditlog-sep"
                                      aria-hidden="true"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-6"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                                        />
                                      </svg>
                                    </div>
                                    <div className="sh_auditlog-chip sh_auditlog-chip-new">
                                      {entry.newValue}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="sh_auditlog-json-wrap">
                                    <div className="sh_auditlog-json-section">
                                      <div className="sh_auditlog-json-label sh_auditlog-json-old">
                                        Old JSON data
                                      </div>
                                      <pre className="sh_auditlog-json-block sh_auditlog-json-block-old">
                                        <code>{entry.oldJson}</code>
                                      </pre>
                                    </div>
                                    <div className="sh_auditlog-json-section">
                                      <div className="sh_auditlog-json-label sh_auditlog-json-new">
                                        Changed JSON data
                                      </div>
                                      <pre className="sh_auditlog-json-block sh_auditlog-json-block-new">
                                        <code>{entry.newJson}</code>
                                      </pre>
                                    </div>
                                  </div>
                                )}

                                <div className="sh_auditlog-meta">
                                  <span>Updated by {entry.updatedBy}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div
                      className={`sh_auditlog-empty ${
                        auditLogEntries.length === 0 ? "" : "d-none"
                      }`}
                      id="auditEmptyState"
                    >
                      <div className="text-center py-5">
                        <div className="fw-semibold">
                          {auditLogLoading
                            ? "Loading audit entries..."
                            : "No audit entries found"}
                        </div>
                        <div className="text-muted small">
                          Try adjusting your filters.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {auditTotalRecords > 0 && (
                <div className="modal-footer flex-shrink-0 p-0 border-top bg-white">
                  <div className="d-flex justify-content-between align-items-center mt-1 sh_cardFooter sh_cardbglight sh_pagination w-100 mb-0 px-3 py-2">
                    <div className="d-flex align-items-center">
                      <span className="text-sm mr-2">Showing</span>
                      <div className="dropdown">
                        <button
                          className="btn bg-white input-group-text mr-2 dropdown-toggle text-sm"
                          type="button"
                          id="auditLogPopupPerPageDropdown"
                          data-bs-toggle="dropdown"
                          disabled={auditLogLoading}
                        >
                          {auditPerPage}
                        </button>
                        <div
                          className="dropdown-menu text-sm"
                          aria-labelledby="auditLogPopupPerPageDropdown"
                        >
                          {[10, 25, 50, 100].map((v) => (
                            <button
                              key={v}
                              type="button"
                              className={`dropdown-item ${
                                auditPerPage === v ? "selected" : ""
                              }`}
                              onClick={() => handleAuditPerPagePick(v)}
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                      <span className="text-sm">of {auditTotalRecords} items</span>
                    </div>

                    <div className="d-flex align-items-center">
                      <ul
                        className={`sh_paginationbox mb-1 d-flex list-unstyled${
                          auditLogLoading ? " pe-none opacity-50" : ""
                        }`}
                      >
                        {auditPage !== 1 && (
                          <li className="page-item cursor-pointer">
                            <a
                              aria-label="First"
                              title="Go to First Page"
                              className="page-link"
                              onClick={() => handleAuditNavPage(1)}
                            >
                              <span aria-hidden="true">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  height="16px"
                                  viewBox="0 -960 960 960"
                                  width="16px"
                                  fill="#023292"
                                >
                                  <path d="M440-240 200-480l240-240 56 56-183 184 183 184-56 56Zm264 0L464-480l240-240 56 56-183 184 183 184-56 56Z"></path>
                                </svg>
                              </span>
                            </a>
                          </li>
                        )}

                        {auditPage !== 1 && (
                          <li className="page-item cursor-pointer">
                            <a
                              aria-label="Previous"
                              title="Go to Previous Page"
                              className="page-link"
                              onClick={() => handleAuditNavPage(auditPage - 1)}
                            >
                              <span aria-hidden="true">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  height="16px"
                                  viewBox="0 -960 960 960"
                                  width="16px"
                                  fill="#023292"
                                >
                                  <path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z"></path>
                                </svg>
                              </span>
                            </a>
                          </li>
                        )}

                        {visiblePageNumbers.map((pageNum) => (
                          <li
                            key={pageNum}
                            className={`page-item cursor-pointer ${
                              pageNum === auditPage ? "active" : ""
                            }`}
                          >
                            <a
                              className="page-link action_bg"
                              title={`Go to Page ${pageNum}`}
                              onClick={() => handleAuditNavPage(pageNum)}
                            >
                              {pageNum}
                            </a>
                          </li>
                        ))}

                        {auditPage !== totalPagesNav && (
                          <li className="page-item cursor-pointer">
                            <a
                              aria-label="Next"
                              title="Go to Next Page"
                              className="page-link"
                              onClick={() => handleAuditNavPage(auditPage + 1)}
                            >
                              <span aria-hidden="true">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  height="16px"
                                  viewBox="0 -960 960 960"
                                  width="16px"
                                  fill="#023292"
                                >
                                  <path d="M522-480 333-669l51-51 240 240-240 240-51-51 189-189Z"></path>
                                </svg>
                              </span>
                            </a>
                          </li>
                        )}

                        {auditPage !== totalPagesNav && (
                          <li className="page-item cursor-pointer">
                            <a
                              aria-label="Last"
                              title="Go to Last Page"
                              className="page-link"
                              onClick={() => handleAuditNavPage(totalPagesNav)}
                            >
                              <span aria-hidden="true">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  height="16px"
                                  viewBox="0 -960 960 960"
                                  width="16px"
                                  fill="#023292"
                                >
                                  <path d="M381-480 192-669l51-51 240 240-240 240-51-51 189-189Zm285 0L477-669l51-51 240 240-240 240-51-51 189-189Z"></path>
                                </svg>
                              </span>
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/*
        Backdrop needs to be adjacent sibling to `#auditlogscreen`
        for the existing `#auditlogscreen + .modal-backdrop.fade.show` CSS
        to apply correctly.
      */}
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default AuditLogPopup;

